/**
 * Data Quality — Recall Snapshot monitoring.
 *
 * Tabs: Recall Snapshots | Validation Failures | Schema Drift.
 * The Recall Snapshots tab supports a "combined view" (all tenants) by default,
 * with a tenant filter that scopes every section (KPIs, trend, ribbon, issues,
 * asset table, schedule, freshness) to the selected tenant.
 */
import React, { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@vehix/portal/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { useQueryClient } from '@tanstack/react-query';
import { SecurityBoundary } from '@vehix/portal/lib/security';
import { SystemRole } from '@vehix/portal/types';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import {
  recallSnapshotKeys,
  useDataCollectionSchedules,
  useFreshnessSLO,
  useHealthBannerSummary,
  usePerTenantAssetTable,
  useRefreshExecutions,
  useRefreshOutcomeRibbon,
  useScheduleContext,
  useTopIssues,
  useVolumeTrendWithBand
} from '@vehix/shared/admin/hooks/useRecallSnapshotMonitoring';
import HealthBanner from '@vehix/shared/admin/components/recallSnapshots/HealthBanner';
import FreshnessContractCard from '@vehix/shared/admin/components/recallSnapshots/FreshnessContractCard';
import ScheduleContextCard from '@vehix/shared/admin/components/recallSnapshots/ScheduleContextCard';
import SnapshotKPIStrip from '@vehix/shared/admin/components/recallSnapshots/SnapshotKPIStrip';
import VolumeTrendChart from '@vehix/shared/admin/components/recallSnapshots/VolumeTrendChart';
import RefreshOutcomeRibbon from '@vehix/shared/admin/components/recallSnapshots/RefreshOutcomeRibbon';
import TopIssuesList from '@vehix/shared/admin/components/recallSnapshots/TopIssuesList';
import PerTenantAssetTable from '@vehix/shared/admin/components/recallSnapshots/PerTenantAssetTable';
import AssetDetailDrawer from '@vehix/shared/admin/components/recallSnapshots/AssetDetailDrawer';
import type { PerOemAsset, TopIssue } from '@vehix/shared/types/recallSnapshots';

type PollingPreset = 'off' | '30s' | '2m' | '5m';

const POLL_INTERVALS: Record<PollingPreset, number | undefined> = {
  off: undefined,
  '30s': 30_000,
  '2m': 120_000,
  '5m': 300_000
};

const ALL_TENANTS = '__all__';

const DataQualityPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [polling, setPolling] = useState<PollingPreset>('2m');
  const [tenantFilter, setTenantFilter] = useState<string>(ALL_TENANTS);
  const [selectedAsset, setSelectedAsset] = useState<PerOemAsset | null>(null);

  const pollMs = POLL_INTERVALS[polling];

  const tenantsQuery = useAllTenants();
  const assetTable = usePerTenantAssetTable(pollMs);
  const executionsQuery = useRefreshExecutions(2000, pollMs);
  const schedulesQuery = useDataCollectionSchedules();

  const allExecutions = executionsQuery.data ?? [];
  const allSchedules = schedulesQuery.data ?? [];
  const allGroups = assetTable.data;

  // Apply the tenant filter once at the page level — all derived selectors
  // operate on the filtered subset, keeping every section in lockstep.
  const filteredGroups = useMemo(
    () => (tenantFilter === ALL_TENANTS ? allGroups : allGroups.filter(g => g.tenant_id === tenantFilter)),
    [allGroups, tenantFilter]
  );
  const filteredExecutions = useMemo(
    () =>
      tenantFilter === ALL_TENANTS
        ? allExecutions
        : allExecutions.filter(e => e.tenant_id === tenantFilter),
    [allExecutions, tenantFilter]
  );
  const filteredSchedules = useMemo(
    () =>
      tenantFilter === ALL_TENANTS
        ? allSchedules
        : allSchedules.filter(s => s.tenant_id === tenantFilter),
    [allSchedules, tenantFilter]
  );

  const tenantsLookup = useMemo(() => {
    const map = new Map<string, string>();
    (tenantsQuery.data ?? []).forEach(t => map.set(t.id, t.tenant_name ?? ''));
    return map;
  }, [tenantsQuery.data]);

  const banner = useHealthBannerSummary(filteredGroups, filteredExecutions);
  const slo = useFreshnessSLO(filteredExecutions, filteredSchedules);
  const scheduleCtx = useScheduleContext(filteredSchedules, filteredExecutions);
  const trendPoints = useVolumeTrendWithBand(filteredExecutions);
  const ribbon = useRefreshOutcomeRibbon(filteredExecutions, tenantsLookup, 20);
  const issues = useTopIssues(filteredGroups);

  // The OLDEST snapshot date across everything in view, not the newest.
  //
  // This feeds the freshness contract card, which sits beside a "within SLO" pill. The
  // max was the single most-recently-crawled tenant, so one healthy tenant made the whole
  // fleet look current — exactly the wrong direction for a card whose job is to say
  // whether the data behind these numbers can be trusted. Snapshot dates are tenant-scoped
  // and can legitimately sit weeks apart, so the worst one is the only honest summary.
  const oldestSnapshotDate = useMemo(() => {
    let oldest: string | null = null;
    for (const g of filteredGroups) {
      for (const o of g.oems) {
        if (o.latest_snapshot_date && (!oldest || o.latest_snapshot_date < oldest)) {
          oldest = o.latest_snapshot_date;
        }
      }
    }
    return oldest;
  }, [filteredGroups]);

  const handleManualRefresh = (): void => {
    queryClient.invalidateQueries({ queryKey: recallSnapshotKeys.all });
  };

  const handleSelectIssue = (issue: TopIssue): void => {
    const asset = filteredGroups
      .find(g => g.tenant_id === issue.tenant_id)
      ?.oems.find(o => o.oem === issue.oem);
    if (asset) setSelectedAsset(asset);
  };

  const tenantOptions = useMemo(() => {
    const list = (tenantsQuery.data ?? [])
      .map(t => ({ id: t.id, name: t.tenant_name ?? '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [tenantsQuery.data]);

  return (
    <SecurityBoundary
      requiredSystemRole={SystemRole.SUPER_ADMIN}
      auditPageAccess='data-quality'
      tenantIsolation={false}
    >
      <div className='flex h-full w-full flex-col overflow-y-auto p-6'>
        <Tabs defaultValue='recall-snapshots' className='flex flex-col flex-1'>
          <TabsList className='self-start'>
            <TabsTrigger value='recall-snapshots'>Recall snapshots</TabsTrigger>
            <TabsTrigger value='validation-failures'>Validation failures</TabsTrigger>
            <TabsTrigger value='schema-drift'>Schema drift</TabsTrigger>
          </TabsList>

          <TabsContent value='recall-snapshots' className='space-y-4 mt-4'>
            {/* Toolbar: tenant filter + polling + manual refresh */}
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='flex items-center gap-2'>
                <span className='text-xs text-muted-foreground'>Tenant:</span>
                <Select value={tenantFilter} onValueChange={setTenantFilter}>
                  <SelectTrigger className='h-8 w-56 text-xs'>
                    <SelectValue placeholder='All tenants' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_TENANTS}>All tenants (combined)</SelectItem>
                    {tenantOptions.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='flex items-center gap-2'>
                <Select value={polling} onValueChange={v => setPolling(v as PollingPreset)}>
                  <SelectTrigger className='h-8 w-32 text-xs'>
                    <SelectValue placeholder='Poll' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='off'>Polling: off</SelectItem>
                    <SelectItem value='30s'>Poll: 30s</SelectItem>
                    <SelectItem value='2m'>Poll: 2m</SelectItem>
                    <SelectItem value='5m'>Poll: 5m</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleManualRefresh}
                  disabled={assetTable.isLoading}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 mr-1.5 ${assetTable.isLoading ? 'animate-spin' : ''}`}
                  />
                  Refresh
                </Button>
              </div>
            </div>

            {/* A. Health Banner */}
            <HealthBanner summary={banner} isLoading={assetTable.isLoading} />

            {/* B + C: Freshness Contract + Schedule Context */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <FreshnessContractCard slo={slo} oldestSnapshotDate={oldestSnapshotDate} />
              <ScheduleContextCard context={scheduleCtx} />
            </div>

            {/* D. KPI Strip */}
            <SnapshotKPIStrip summary={banner} loading={assetTable.isLoading} />

            {/* E + F: Volume Trend + Refresh Outcome Ribbon */}
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-3'>
              <div className='lg:col-span-2'>
                <VolumeTrendChart points={trendPoints} />
              </div>
              <div>
                <RefreshOutcomeRibbon entries={ribbon} />
              </div>
            </div>

            {/* G. Top Issues */}
            <TopIssuesList issues={issues} onSelect={handleSelectIssue} />

            {/* H. Per-Tenant Asset Table */}
            <PerTenantAssetTable
              groups={filteredGroups}
              isLoading={assetTable.isLoading}
              onSelectAsset={setSelectedAsset}
            />
          </TabsContent>

          <TabsContent value='validation-failures' className='mt-4'>
            <div className='flex h-64 items-center justify-center rounded-md border border-dashed'>
              <p className='text-sm text-muted-foreground'>
                Coming soon — schema-violation summaries and per-pipeline validation metrics.
              </p>
            </div>
          </TabsContent>

          <TabsContent value='schema-drift' className='mt-4'>
            <div className='flex h-64 items-center justify-center rounded-md border border-dashed'>
              <p className='text-sm text-muted-foreground'>
                Coming soon — column-level drift signals and historical schema diffs.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Side-panel drill-down */}
        <AssetDetailDrawer
          asset={selectedAsset}
          executions={allExecutions}
          onClose={() => setSelectedAsset(null)}
        />
      </div>
    </SecurityBoundary>
  );
};

export default DataQualityPage;
